/**
 * Journey status metadata — single source of truth for the dashboard.
 *
 * Keys are the raw backend enum values (Journey.journeyStatus); always send the
 * raw key to the API and only use `label` for display.
 *
 * - label:           human-readable text for chips, filter options and cards.
 * - chip:            StatusChip type ("success" | "warning" | "error" | "end").
 * - monitoringColor: Tailwind classes for the Monitoring volumetrics card.
 *
 * NOTE: tailwind.config.js scans src/constants so the monitoringColor classes
 * here are not purged.
 *
 * Insertion order is the order the status filter lists options in.
 */
export const JOURNEY_STATUS_META = {
  COMPLETED: {
    label: "Completed",
    chip: "success",
    monitoringColor: "bg-emerald-50 text-emerald-700 border-emerald-200",
  },
  NOT_COMPLETED: {
    label: "Not Completed",
    chip: "warning",
    monitoringColor: "bg-amber-50 text-amber-700 border-amber-200",
  },
  FAILED: {
    label: "Failed",
    chip: "error",
    monitoringColor: "bg-red-50 text-red-700 border-red-200",
  },
  MEETING_CONNECTION_FAILED: {
    label: "Meeting Connection Failed",
    chip: "error",
    monitoringColor: "bg-neutral-50 text-neutral-700 border-neutral-200",
  },
  USER_DISAGREED_TO_PERSONAL_DETAILS: {
    label: "User Disagreed to Personal Details",
    chip: "error",
    monitoringColor: "bg-indigo-50 text-indigo-700 border-indigo-200",
  },
  USER_DISAGREED_TO_POLICY_DETAILS: {
    label: "User Disagreed to Policy Details",
    chip: "error",
    monitoringColor: "bg-purple-50 text-purple-700 border-purple-200",
  },
  VIDEO_CONSENT_VERIFICATION_ATTEMPTS_EXHAUSTED: {
    label: "Video Consent Verification Attempts Exhausted",
    chip: "error",
    monitoringColor: "bg-orange-50 text-orange-700 border-orange-200",
  },
  VIDEO_CONSENT_PROCESS_ABORTED: {
    label: "Video Consent Process Aborted",
    chip: "error",
    monitoringColor: "bg-rose-50 text-rose-700 border-rose-200",
  },
  VIDEO_CONSENT_FREEZE_LIMIT_REACHED: {
    label: "Video Consent Freeze Limit Reached",
    chip: "error",
    monitoringColor: "bg-sky-50 text-sky-700 border-sky-200",
  },
  MANUAL_OVERWRITE: {
    label: "Manual Overwrite",
    chip: "warning",
    monitoringColor: "bg-blue-50 text-blue-700 border-blue-200",
  },
  API_OVERWRITE: {
    label: "API Overwrite",
    chip: "warning",
    monitoringColor: "bg-cyan-50 text-cyan-700 border-cyan-200",
  },
  APPROVER_PENDING: {
    label: "Approver Pending",
    chip: "end",
    monitoringColor: "bg-yellow-50 text-yellow-700 border-yellow-200",
  },
  APPROVAL_NOT_REQUIRED: {
    label: "Approval Not Required",
    chip: "success",
    monitoringColor: "bg-slate-50 text-slate-700 border-slate-200",
  },
  COMPLETED_APPROVER_ACCEPTED: {
    label: "Completed (Approver Accepted)",
    chip: "success",
    monitoringColor: "bg-lime-50 text-lime-700 border-lime-200",
  },
  FAILED_APPROVER_REJECTED: {
    label: "Failed (Approver Rejected)",
    chip: "error",
    monitoringColor: "bg-pink-50 text-pink-700 border-pink-200",
  },
  COMPLETED_APPROVER_ADHOC_ACCEPTED: {
    label: "Completed (Approver Ad-hoc Accepted)",
    chip: "success",
    monitoringColor: "bg-teal-50 text-teal-700 border-teal-200",
  },
  SUSPECTED_FRAUD: {
    label: "Suspected Fraud",
    chip: "error",
    monitoringColor: "bg-violet-50 text-violet-700 border-violet-200",
  },
  VIDEO_VERIFICATION_FAILED: {
    label: "Video Verification Failed",
    chip: "error",
    monitoringColor: "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200",
  },
};

/** Raw status keys, for use in comparisons instead of string literals. */
export const JOURNEY_STATUS = Object.freeze(
  Object.keys(JOURNEY_STATUS_META).reduce((acc, key) => {
    acc[key] = key;
    return acc;
  }, {})
);

/** Every status, in the order the status filter lists them. */
export const ALL_STATUSES = Object.keys(JOURNEY_STATUS_META);

/** Completed family — gates PDF/KYP sections and resync actions in the side panel. */
export const COMPLETED_STATUSES = [
  JOURNEY_STATUS.COMPLETED,
  JOURNEY_STATUS.COMPLETED_APPROVER_ACCEPTED,
  JOURNEY_STATUS.COMPLETED_APPROVER_ADHOC_ACCEPTED,
];

/** Customer-disagreement statuses — show the disagreement reason section. */
export const DISAGREED_STATUSES = [
  JOURNEY_STATUS.USER_DISAGREED_TO_PERSONAL_DETAILS,
  JOURNEY_STATUS.USER_DISAGREED_TO_POLICY_DETAILS,
];

/**
 * Monitoring volumetrics card order. The first 14 are the cards Monitoring
 * always showed (same order); the last four were missing and are appended.
 * The backend's COMPLETED volumetric is an aggregate of COMPLETED_STATUSES.
 */
export const MONITORING_STATUSES = [
  JOURNEY_STATUS.NOT_COMPLETED,
  JOURNEY_STATUS.APPROVAL_NOT_REQUIRED,
  JOURNEY_STATUS.COMPLETED,
  JOURNEY_STATUS.VIDEO_CONSENT_PROCESS_ABORTED,
  JOURNEY_STATUS.FAILED,
  JOURNEY_STATUS.VIDEO_CONSENT_VERIFICATION_ATTEMPTS_EXHAUSTED,
  JOURNEY_STATUS.USER_DISAGREED_TO_POLICY_DETAILS,
  JOURNEY_STATUS.USER_DISAGREED_TO_PERSONAL_DETAILS,
  JOURNEY_STATUS.MANUAL_OVERWRITE,
  JOURNEY_STATUS.MEETING_CONNECTION_FAILED,
  JOURNEY_STATUS.API_OVERWRITE,
  JOURNEY_STATUS.APPROVER_PENDING,
  JOURNEY_STATUS.COMPLETED_APPROVER_ADHOC_ACCEPTED,
  JOURNEY_STATUS.VIDEO_VERIFICATION_FAILED,
  JOURNEY_STATUS.VIDEO_CONSENT_FREEZE_LIMIT_REACHED,
  JOURNEY_STATUS.SUSPECTED_FRAUD,
  JOURNEY_STATUS.COMPLETED_APPROVER_ACCEPTED,
  JOURNEY_STATUS.FAILED_APPROVER_REJECTED,
];

/** Human-readable label; falls back to the raw value for unknown statuses. */
export const getStatusLabel = (status) =>
  JOURNEY_STATUS_META[status]?.label ?? status;

/** StatusChip type, or null for an unknown status. */
export const getStatusChipType = (status) =>
  JOURNEY_STATUS_META[status]?.chip ?? null;

export const isCompletedStatus = (status) => COMPLETED_STATUSES.includes(status);

export const isDisagreedStatus = (status) => DISAGREED_STATUSES.includes(status);
